import { api } from '../api/http'

export type ClientDevice = {
  platform: string
  lang: string
  screenW: number
  screenH: number
  viewportW: number
  viewportH: number
  dpr: number
  displayMode: 'browser' | 'standalone' | 'minimal-ui' | 'fullscreen'
  touch: boolean
  coarse: boolean
  orientationLock: boolean
  maxTouchPoints: number
  /** Chrome high-entropy hint. Empty when the browser will not say. */
  model: string
  /** Android or Chrome OS version from the same hint. */
  osVersion: string
}

const NOTED_KEY = 'dc-device-noted'

/** What this browser can tell us about the phone or computer. */
export function readClientDevice(): ClientDevice {
  const nav = navigator as Navigator & {
    userAgentData?: { platform?: string }
    standalone?: boolean
  }
  let displayMode: ClientDevice['displayMode'] = 'browser'
  const modes: ClientDevice['displayMode'][] = [
    'standalone',
    'minimal-ui',
    'fullscreen',
    'browser',
  ]
  for (const mode of modes) {
    if (window.matchMedia(`(display-mode: ${mode})`).matches) {
      displayMode = mode
      break
    }
  }
  if (nav.standalone) displayMode = 'standalone'
  const orientation = (screen as { orientation?: { lock?: unknown } }).orientation
  const coarse = window.matchMedia('(pointer: coarse)').matches
  return {
    platform: (nav.userAgentData?.platform || navigator.platform || '').slice(0, 64),
    lang: (navigator.language || '').slice(0, 16),
    screenW: Math.round(screen.width || 0),
    screenH: Math.round(screen.height || 0),
    viewportW: Math.round(window.innerWidth || 0),
    viewportH: Math.round(window.innerHeight || 0),
    dpr: window.devicePixelRatio || 1,
    displayMode,
    touch: navigator.maxTouchPoints > 0 || coarse,
    coarse,
    orientationLock: typeof orientation?.lock === 'function',
    maxTouchPoints: navigator.maxTouchPoints || 0,
    model: '',
    osVersion: '',
  }
}

/**
 * The frozen user agent says `Android 10; K` on every modern phone.
 * Chrome can still name the model and the real OS version.
 */
async function readDeviceModel(): Promise<{ model: string; osVersion: string }> {
  const ua = (
    navigator as Navigator & {
      userAgentData?: {
        getHighEntropyValues?: (
          hints: string[],
        ) => Promise<{ model?: string; platformVersion?: string }>
      }
    }
  ).userAgentData
  const read = ua?.getHighEntropyValues
  if (!read) return { model: '', osVersion: '' }
  try {
    const data = await Promise.race([
      read.call(ua, ['model', 'platformVersion']),
      new Promise<null>((resolve) => {
        window.setTimeout(() => resolve(null), 1500)
      }),
    ])
    if (!data) return { model: '', osVersion: '' }
    return {
      model: (data.model || '').trim().slice(0, 64),
      osVersion: (data.platformVersion || '').trim().slice(0, 32),
    }
  } catch {
    return { model: '', osVersion: '' }
  }
}

/** Screen size plus the model, when Chrome will say it. */
export async function readClientDeviceAsync(): Promise<ClientDevice> {
  const hints = await readDeviceModel()
  return { ...readClientDevice(), ...hints }
}

/** Once per tab, after the account is loaded. Failures retry next visit. */
export function noteClientDevice(token: string | null): void {
  if (!token || typeof window === 'undefined') return
  try {
    if (sessionStorage.getItem(NOTED_KEY) === '1') return
    sessionStorage.setItem(NOTED_KEY, '1')
  } catch {
    /* private mode still posts */
  }
  void readClientDeviceAsync()
    .then((device) =>
      api('/account/device', {
        method: 'POST',
        token,
        quiet: true,
        body: JSON.stringify({ device }),
      }),
    )
    .catch(() => {
      try {
        sessionStorage.removeItem(NOTED_KEY)
      } catch {
        /* ignore */
      }
    })
}
